
import Page from '@root/common/base/Page';
import GridSkeletonLoader from '@root/common/components/base/loader/GridSkeletonLoader.vue';
import Author from '@root/modules/author/components/Author.vue';
import AuthorBreadcrumbs from '@root/modules/author/components/AuthorBreadcrumbs.vue';

import { Data as AuthorData, GetAuthors } from '@root/modules/author/types/author';
import AuthorService from '@root/modules/author/services/Author.service';
import AuthorsService from '@root/modules/author/services/Authors.service';
import getPageAdsSettings from '@root/modules/author/utils/getPageAdsSettings';
import getAliasByLocale from '@root/common/utils/getAliasByLocale';
import striptags from 'striptags';

export default Page.extend({
  components: {
    Author,
    AuthorBreadcrumbs,
    GridSkeletonLoader,
  },
  data() {
    return {
      authors: [] as AuthorData[],
      loadingAuthors: false,
      offset: 0,
      limit: 20,
      shoudLoadMoreAuthors: true,
    };
  },
  async fetch() {
    const variables: GetAuthors['variables'] = {
      limit: this.limit,
      offset: this.offset,
    };

    try {
      // Handle d1.0 authors redirect
      if (this.$route.query.id) {
        const authorService = new AuthorService({ req: this.$ssrContext?.req });
        const id = [parseInt(this.$route.query.id)];
        const author = await authorService.fetch({ id });
        if (author) {
          const { base } = this.$channelConfig('meta');
          const { locale } = this.$channelConfig('settings');
          const path = getAliasByLocale(locale, 'author');
          const redirectPath = `${base.baseUrl}/${path}/${author.slug}`;

          this.handlePageRedirect(redirectPath);
        }
      }

      const authorsService = new AuthorsService({ req: this.$ssrContext?.req });
      const authors = await authorsService.fetch(variables, true);

      if (!authors?.length) {
        authorsService.handleError({ message: 'authors.error.not_found', statusCode: 404 });
        return;
      }

      this.authors = authors;

      this.setPageAdsSettings(getPageAdsSettings({ $channelConfig: this.$channelConfig }));
    } catch (e) {
      this.handlePageError(e);
    }
  },
  methods: {
    async loadMoreAuthors() {
      if (this.loadingAuthors || !this.shoudLoadMoreAuthors) {
        return;
      }

      const d = document.documentElement;
      const w = document.querySelector('.authors__list') as HTMLElement;
      if (w) {
        const offsetThreshold = d.clientHeight + 500;
        const userHasScrolled = window.scrollY > 200;
        const offset = window.scrollY + offsetThreshold;

        if (userHasScrolled && offset >= w.offsetHeight) {
          this.loadingAuthors = true;

          const variables = {
            limit: this.limit,
            offset: this.offset + this.limit,
          };

          const authorsService = new AuthorsService({ req: this.$ssrContext?.req });
          const authors = await authorsService.fetch(variables, true);

          if (authors?.length) {
            this.authors = [...this.authors, ...authors];
            this.offset = this.offset + this.limit;
          } else {
            this.shoudLoadMoreAuthors = false;
          }

          this.loadingAuthors = false;
        }
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.loadMoreAuthors);
  },
  async mounted() {
    window.addEventListener('scroll', this.loadMoreAuthors);
  },
  head() {
    const meta = this.getPageMeta();
    const title: string = this.$t('authors.title');
    meta.setTitle({ title });

    const description: string = striptags(this.$t('authors.description'));
    meta.description = meta.ogDescription = description;

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
